import React from 'react';
import { css } from '@emotion/react';
import {
  PublicTeamFragment,
  useVisibleSetsQuery,
  PublicSetAndSessionFragment,
  PublicWebinarDescriptionFragment,
} from '$/GraphQL/schema/schema';
import Loading from '$/components/Loading';
import VideoPreview from '$/components/VideoPreview/VideoPreview';
import { readableColorOnBackground } from '$/utils/colors';
import { useScrollToBottom } from '$/hooks/useScrollToBottom';
import { useList } from '$/hooks/useList';
import { getLocalI18n } from '$/shared/frontend/utils/i18n';
import { useCompanySSRContext } from '$/components/pages/company';
import { useEwebinarSession } from '$/hooks/useEwebinarSession';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import DangerouslySetInnerHTML from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { markdownToText } from '$/shared/frontend/utils/MarkdownConverter';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import { useServerContext } from '$/hooks/useServerDateNow';
import Button from '$/components/ui/Button';
import Text from '$/components/ui/Text';
import Settings from '$/components/ui/Settings';
import config from '$/config';
import { t } from '@lingui/macro';
import { PoweredByEWebinar } from '$/components/Logo';
import { useLocalI18n } from '$/hooks/useLocalI18n';

const ShareAllWebinarCard: React.FC<{
  ewebinar: PublicWebinarDescriptionFragment;
  set: PublicSetAndSessionFragment;
}> = ({ ewebinar, set }) => {
  const { md: isMd } = useWindowSizeInfo();
  const { serverDateNow } = useServerContext();

  const [hostedByString, setHostedByString] = React.useState('Hosted by');
  React.useEffect(() => {
    const loadLanguage = async (language: string): Promise<void> => {
      try {
        const localI18n = await getLocalI18n(language);
        const text = t(localI18n)`Hosted by`;
        setHostedByString(text);
      } catch (e) {
        console.error(e);
      }
    };
    loadLanguage(ewebinar.viewerRoomSettings.language ?? 'en');
  }, [ewebinar.viewerRoomSettings.language]);
  const { sessions: ewebinarSession } = useEwebinarSession({
    ewebinar,
    timezone: LocalTimeZone.name,
    serverDateNow,
  });
  // hide if there is no session
  if (
    !config.isSSR &&
    ((ewebinarSession && !ewebinarSession?.sessions) || ewebinarSession?.sessions.length === 0)
  ) {
    return null;
  }

  const btnText = ewebinar.registrationFormSettings.registerButton
    ? markdownToText(ewebinar.registrationFormSettings.registerButton)
    : 'Register now!';

  const bgColor = ewebinar?.actionColor || ewebinar?.primaryColor || '#39A1B2';
  const color = readableColorOnBackground(bgColor);

  return (
    <a
      href={set.urls.registration}
      target='_blank'
      className='WebinarListItem bg-white mt-6 p-6 w-full rounded-lg shadow-md flex relative flex-col md:flex-row'
    >
      <div
        className='flex flex-col md:mr-8'
        css={css`
          ${isMd
            ? `
            min-width: 300px;
          `
            : `
            min-width: 100%;
          `}
        `}
      >
        <div
          className='rounded-lg overflow-hidden'
          css={[
            isMd
              ? css`
                  height: calc(300px * 0.525);
                `
              : css`
                  height: calc((min(640px, 100vw) - 4rem) * 0.525);
                `,
          ]}
        >
          <VideoPreview
            setId={ewebinar.set.id}
            thumbnailWebinar={ewebinar}
            setSocialMediaTags={set.socialMediaTags}
            showImageOnly
            thumbnailOnly
          />
        </div>
        {isMd && (
          <Button.rounded
            className='justify-center mt-4 py-2'
            css={css`
              height: 2.5rem;
            `}
            theme={{
              fg: color,
              fgHover: color,
              bg: bgColor,
              bgHover: bgColor,
            }}
          >
            {btnText}
          </Button.rounded>
        )}
      </div>
      <div className='flex flex-col w-full'>
        <div
          css={css`
            min-height: 1.2rem;
          `}
        >
          <div className='flex justify-between flex-col md:flex-row'>
            <Text.headline className='py-0 pr-4 md:text-left text-center text-2xl my-1e md:mt-0 md:mb-0'>
              {set.socialMediaTags.title}
            </Text.headline>
          </div>
          {ewebinar.presenters && ewebinar.presenters.length > 0 && (
            <Text.body className='text-gray-2 text-center md:text-left md:mt-1/2e'>
              {`${hostedByString} `}
              <span className='text-gray-3 font-medium'>
                {ewebinar.presenters.map((p) => p.name).join(', ')}
              </span>
            </Text.body>
          )}
          <Text.body
            className='w-full overflow-hidden text-center mt-1e md:text-left'
            css={css`
              color: #5b5e6d;
              display: -webkit-box;
              -webkit-line-clamp: 5;
              line-height: 21px;
              -webkit-box-orient: vertical;
              overflow: hidden;
            `}
          >
            <DangerouslySetInnerHTML rawHTML={markdownToText(set.socialMediaTags.description)} />
          </Text.body>
        </div>
        {!isMd && (
          <Button.rounded
            className='justify-center my-1_5e py-4'
            theme={{
              fg: color,
              fgHover: color,
              bg: bgColor,
              bgHover: bgColor,
            }}
          >
            {btnText}
          </Button.rounded>
        )}
      </div>
    </a>
  );
};

interface Props {
  team?: PublicTeamFragment;
}

const PublishedWebinarList: React.FunctionComponent<Props> = ({ team }) => {
  const { isSSR, team: ssrTeamData } = useCompanySSRContext();
  const { setServerDate } = useServerContext();

  const hasSSRData = Boolean(ssrTeamData);

  const variables = React.useMemo(
    () => ({
      teamId: team?.id ?? '',
      nextCursor: '',
    }),
    [team]
  );
  const setsQuery = useList({
    queryHook: useVisibleSetsQuery,
    getVars: (nextCursor?: string) => ({
      ...variables,
      nextCursor: nextCursor ?? '',
    }),
    cursorPath: 'visibleSets.nextCursor',
    dataPath: 'visibleSets.sets',
    skip: isSSR,
  });
  const containerRef = useScrollToBottom(
    setsQuery.onLoadMore,
    Boolean(setsQuery.data?.visibleSets.nextCursor),
    true
  );

  React.useEffect(() => {
    const now = setsQuery.data?.visibleSets?.sets?.[0]?.now;
    if (!now) return;
    setServerDate(new Date(now));
  }, [setServerDate, setsQuery.data?.visibleSets?.sets]);

  const sets = setsQuery.data?.visibleSets.sets ?? ssrTeamData?.publicSets ?? [];
  const language = sets?.[0]?.publicWebinar?.viewerRoomSettings?.language ?? 'en';

  const i18n = useLocalI18n(language);

  if (!hasSSRData && (!setsQuery.data || !setsQuery.data.visibleSets)) {
    return <Loading query={setsQuery} />;
  }

  return (
    <>
      <div ref={containerRef}>
        {sets.map((set: PublicSetAndSessionFragment) => (
          <ShareAllWebinarCard key={set.id} ewebinar={set.publicWebinar!} set={set} />
        ))}
        {setsQuery.data?.visibleSets.nextCursor && (
          <Loading
            css={css`
              position: static;
              margin: 20px auto;
            `}
          />
        )}
        {sets.length === 0 && (
          <Settings.Title className='mt-3e px-6 md:px-0 text-lg md:text-2xl'>
            No webinars available yet but stay tuned...
          </Settings.Title>
        )}
      </div>

      <div className='py-5 md:py-8 mt-auto'>
        {i18n && (
          <PoweredByEWebinar
            i18nProvided={i18n}
            hideBranding={team?.settings.hideBranding}
            teamId={team?.id ?? '0' /* If we have no team pass dummy team to just redirect */}
          />
        )}
      </div>
    </>
  );
};

export default PublishedWebinarList;
